"use client";

import { useEffect, useRef, useState } from "react";

import { useTranslations } from "next-intl";

import { Locale } from "@/shared.i18n/config";
import { AppContainer } from "@/stf.ui/ui.app-container";

import { type ServerDataOutput } from "../data.subscription-data";
import { SelectorPackage } from "../ui.selector-package";
import { SectionTitle } from "./prim.subscription-detail-section-layout";
import { BuyNowInfoBox } from "./ui.buy-now-info-box";
import { NextShipBanner } from "./ui.next-ship-banner";
import { BoxSizeSelector } from "./ui.selector-box-size";
import { BuyNowButton } from "./ui.selector-buy-now-button";
import { DurationSelector } from "./ui.selector-duration";
import { FrequencySelector } from "./ui.selector-frequency";
import { ShippingScheduleDialog } from "./ui.shipping-schedule-dialog";
import { MobileSummarySection, SummarySection } from "./ui.summary-section";

const useIntersectionObserver = () => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { return setInView(entry.isIntersecting); },
      {
        root: null,
        threshold: 0,
      },
    );

    if (parentRef.current) {
      observer.observe(parentRef.current);
    }

    return () => {
      if (parentRef.current) {
        observer.unobserve(parentRef.current);
      }
    };
  }, []);

  return {
    inView, parentRef,
  };
};

interface SubscriptionSectionProps {
  data: ServerDataOutput;
  locale: Locale;
  serverNow: number;
}
export const SubscriptionSection = (props: SubscriptionSectionProps) => {
  const { data, locale, serverNow } = props;

  const t = useTranslations("stf_m_subscription.screen_subscription");
  const { inView, parentRef } = useIntersectionObserver();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [now, setNow] = useState(serverNow);

  useEffect(() => {
    setNow(Date.now());
  }, []);

  return (
    <AppContainer size="3">
      <div className="flex flex-col" ref={parentRef}>
        <SectionTitle>
          {t.rich("start_your_coffee_journey", {
            bold: (chunks) => {
              return <span className="text-accent-11">{chunks}</span>;
            },
          })}
        </SectionTitle>
        <NextShipBanner
          className="my-8"
          data={data}
          now={now}
          onClick={() => { return setScheduleOpen(true); }}
        />
        <ShippingScheduleDialog
          data={data}
          now={now}
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
        />
        <div className={`
          relative m-auto grid grid-cols-1 gap-6
          md:grid-cols-2
        `}
        >
          <div className={`
            max-w-screen-xs flex flex-col gap-6
          `}
          >
            <SelectorPackage data={data} locale={locale} />
            <BoxSizeSelector data={data} />
            <FrequencySelector data={data} />
            <DurationSelector data={data} />
          </div>
          <div className={`
            sticky top-32 my-1 hidden w-full flex-col gap-6
            self-start shadow-2 rounded-3 p-6
            md:flex
          `}
          >
            <SummarySection data={data} />
            <BuyNowInfoBox data={data} />
            <BuyNowButton data={data} />
          </div>
          {inView
            ? (
                <div className={`
                  fixed inset-x-0 bottom-0 z-50 flex
                  flex-col
                  md:hidden
                  xs:m-2
                `}
                >
                  <div className={`
                    w-full max-w-screen-xs m-auto flex
                    flex-col bg-accent-1 shadow-4 p-3
                    rounded-3 gap-4
                  `}
                  >
                    <MobileSummarySection data={data} />
                    <BuyNowInfoBox data={data} />
                    <BuyNowButton data={data} />
                  </div>
                </div>
              )
            : null}
        </div>
      </div>
    </AppContainer>
  );
};
